import React from 'react';
import classNames from 'classnames';

import Button from 'components/partials/button/button';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Heading from 'components/partials/heading/heading';

import { useCurrentUser } from 'hooks/api/use-current-user';

interface CurrentUserShape {
  data?: { admin?: boolean };
}

interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  isDelete?: boolean;
  onClick: () => void;
}

interface CriticalElementDetailsHeaderProps {
  isEditMode: boolean;
  onEditClick: () => void;
  customClass?: string;
  isNew: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  activeTab: string;
  actionMenuItems: ActionMenuItem[];
  title?: string;
}

const getDetailsTitle = (
  isNew: boolean,
  isEditMode: boolean,
  title: string | undefined
): string => {
  const elementStatuses = {
    new: 'New Critical Element',
    edit: 'Edit Critical Element',
    read: 'Critical Element Details',
  };

  return (
    title ||
    (isNew && elementStatuses.new) ||
    (isEditMode && elementStatuses.edit) ||
    elementStatuses.read
  );
};

const CriticalElementDetailsHeader = ({
  isEditMode,
  onEditClick,
  customClass = '',
  isNew,
  onCancel,
  onSubmit,
  activeTab,
  actionMenuItems,
  title,
}: CriticalElementDetailsHeaderProps): React.JSX.Element => {
  const currentUser = useCurrentUser() as CurrentUserShape;

  return (
    <div
      className={classNames(
        'flex justify-between items-center gap-x-5 min-h-11',
        customClass
      )}
    >
      <Heading variant="h3" customClass="font-bold">
        {getDetailsTitle(isNew, isEditMode, title)}
      </Heading>

      {activeTab === 'details' &&
        (isNew || isEditMode ? (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="reserve-invert"
              onClick={onCancel}
              data-testid="cancel-critical-element-button"
            >
              Cancel
            </Button>
            <Button
              variant="brand-filled"
              onClick={onSubmit}
              data-testid="save-critical-element-button"
            >
              Save changes
            </Button>
          </div>
        ) : (
          currentUser.data?.admin && (
            <ActionMenu
              onEditClick={onEditClick}
              buttonName="Edit details"
              options={actionMenuItems}
              {...({ size: 'small' } as Record<string, unknown>)}
            />
          )
        ))}
    </div>
  );
};

export default CriticalElementDetailsHeader;
